/**
 * The provider vocabulary — pure data, no React (2026-09-29).
 *
 * Split out of `lib/hooks/use-mail-brand.ts` so plain modules that must stay
 * hook-free (e.g. `lib/api/errors.ts`, which maps an API error to a toast)
 * can name the firm's provider without pulling React Query into their
 * import graph. The hook re-exports everything here, so existing imports of
 * `brandFor` / `MailBrand` from the hook keep working.
 *
 * The API's twin is `apps/api/src/lib/provider-copy.ts` — keep the two
 * `suiteName` / `signInWith` values in step.
 */
import type { WorkspaceProvider } from '@/lib/api/users-me';

export interface MailBrand {
  /** Resolved provider, or null while loading / not yet connected. */
  provider: WorkspaceProvider | null;
  /** The mail application: "Outlook" | "Gmail". */
  mailApp: string;
  /** Its web client, where a deep link lands: "Outlook Web" | "Gmail". */
  mailWebApp: string;
  /** The file store behind case documents: "OneDrive" | "Google Drive". */
  fileApp: string;
  /** Where a working-copy document opens for editing: "Word" | "Google Docs" ("Open in …"). */
  docsApp: string;
  /**
   * The mailbox root, for the "open my mail" button that has no specific
   * message to deep-link to. Every other jump in the app uses a `webLink`
   * the API returned, so this constant is the one place a provider URL is
   * built client-side.
   */
  mailHomeUrl: string;
  /** The product suite, for account / re-consent copy: "Microsoft 365" | "Google Workspace". */
  suiteName: string;
  /** Who the person signs in with: "Microsoft" | "Google" ("Sign in with …"). */
  signInWith: string;
}

const BRANDS: Record<WorkspaceProvider, Omit<MailBrand, 'provider'>> = {
  MICROSOFT: {
    mailApp: 'Outlook',
    mailWebApp: 'Outlook Web',
    fileApp: 'OneDrive',
    docsApp: 'Word',
    mailHomeUrl: 'https://outlook.office.com/mail/',
    suiteName: 'Microsoft 365',
    signInWith: 'Microsoft',
  },
  GOOGLE: {
    mailApp: 'Gmail',
    mailWebApp: 'Gmail',
    fileApp: 'Google Drive',
    docsApp: 'Google Docs',
    mailHomeUrl: 'https://mail.google.com/',
    suiteName: 'Google Workspace',
    signInWith: 'Google',
  },
};

// One frozen object per answer, built once. `brandFor` must return the SAME
// reference for the same provider: hooks put the brand in dependency arrays
// (`useApiErrorToast`'s useCallback, and effects downstream of it), and a new
// object each render would re-create those callbacks and re-run those effects
// on every render.
const RESOLVED: Record<WorkspaceProvider | 'UNKNOWN', MailBrand> = {
  MICROSOFT: Object.freeze({ provider: 'MICROSOFT', ...BRANDS.MICROSOFT }),
  GOOGLE: Object.freeze({ provider: 'GOOGLE', ...BRANDS.GOOGLE }),
  UNKNOWN: Object.freeze({ provider: null, ...BRANDS.MICROSOFT }),
};

/** Unknown provider → the Microsoft wording the UI used unconditionally before. */
export function brandFor(provider: WorkspaceProvider | null | undefined): MailBrand {
  return RESOLVED[provider ?? 'UNKNOWN'];
}
